// Today as a day, and the two pages Home hidden gives a wide window (docs/51; DayView.swift, Glance.swift, the Mac's
// MacDayView.swift and MacGlance.swift). The web reads no calendar, so its day is one free time from now to the day's
// end (dayPlan.ts). A to-do is planned by dragging it onto the free time or a planned to-do, and taken out of the plan
// by dragging it back onto Not Planned. Pro, as the Day view in the apps.

import { Fragment, useEffect, useState, type ReactNode } from "react";
import { currentZone, timeOf } from "../core/days";
import { currentGap, dayPlan, elapsed, freeMinutes, gapMinutes, plannedIn, planTodos, unplanned, unplanTodos, type DayGap } from "../core/dayPlan";
import type { ListSection, Route } from "../core/lists";
import { isList, type Project, type Todo } from "../core/records";
import { locale, t } from "../i18n";
import { useWindow } from "./context";
import { headerDropProps } from "./dnd";
import { ViewSwitch } from "./Menu";
import { deadlineLabel } from "./format";
import { setPrefs, usePrefs } from "./prefs";
import { TodoCard } from "./TodoCard";
import { TodoRow } from "./TodoRow";

/** The clock, twice a minute: enough for the free time and the line. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

/** A length of time as the day line says it: "20 min", "2 h", "1 h 30" (DayPlan.length(minutes:)). */
export function lengthWords(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return t("{minutes} min", { minutes: rest });
  if (rest === 0) return t("{hours} h", { hours });
  return t("{hours} h {minutes}", { hours, minutes: rest });
}

/** Where an overview can stand beside the list: Today as a list, or a project (not a board, a list that goes on, or
 * the birthdays). */
export function offersOverview(route: Route, project: Project | undefined): boolean {
  if (route.kind === "today") return true;
  return project !== undefined && project.layout !== "board" && !isList(project) && project.kind !== "birthdays";
}

/** Whether Today shows as a day: chosen, and with Pro. */
export function useShowsDay(route: Route): boolean {
  const { state } = useWindow();
  const prefs = usePrefs();
  return route.kind === "today" && prefs.todayShowsDay && state.isPro;
}

/** List or Day, beside Today's name (with Pro; the Day view is part of it). */
export function TodayViewSwitch() {
  const { state } = useWindow();
  const prefs = usePrefs();
  if (!state.isPro) return null;
  const choose = (day: boolean) => setPrefs((current) => ({ ...current, todayShowsDay: day }));
  return (
    <ViewSwitch label={t("View")} value={prefs.todayShowsDay ? "day" : "list"} onChange={(value) => choose(value === "day")}
      options={[{ value: "list", label: t("List") }, { value: "day", label: t("Day") }]} />
  );
}

/**
 * Today as a day: Not Planned and the free time with what is planned for it. On one page Not Planned stands on top;
 * on two pages it is the left page and the day the right one, both starting at the same height.
 */
export function DayView({ sections, onComplete }: { sections: ListSection[]; onComplete: (ids: readonly string[]) => void }) {
  const window = useWindow();
  const { state, ui, model } = window;
  const now = useNow();
  const zone = currentZone();
  const today = state.today;
  const plan = dayPlan(today, zone, now);
  // A to-do just ticked keeps its place while its moment plays, as in the list (docs/59, 1b).
  const todos = sections.flatMap((section) => section.todos).filter((todo) => todo.completedAt === undefined || state.lists.isLingering(todo.id));
  const waiting = unplanned(todos, plan.gaps, today, zone);
  const route: Route = { kind: "today" };

  const planInto = (gap: DayGap, ids: readonly string[], before: string | undefined) => {
    const changed = planTodos(ids, gap, before, todos, plan.gaps, today, zone);
    if (changed.length) model.updateMany(changed, t("Plan"));
  };
  const takeOut = (ids: readonly string[]) => {
    const changed = unplanTodos(ids, todos);
    if (changed.length) model.updateMany(changed, t("Not Planned"));
  };
  const row = (todo: Todo, onPlace: (ids: readonly string[], where: "before" | "after") => void) => (ui.open === todo.id
    ? <TodoCard key={todo.id} todo={todo} />
    : <Fragment key={todo.id}><TodoRow todo={todo} route={route} showsPlace={true} inToday={true} onComplete={onComplete} onPlace={onPlace} /></Fragment>);

  const notPlanned = (
    <section className="day-not-planned" aria-label={t("Not Planned")} {...headerDropProps(window, takeOut)}>
      <div className="day-heading"><h2>{t("Not Planned")}</h2><span>{waiting.length}</span></div>
      {waiting.map((todo) => row(todo, (ids) => takeOut(ids)))}
      {waiting.length === 0 && <p className="day-note">{t("Everything is planned. Drag a to-do here to take it out of the plan.")}</p>}
    </section>
  );

  const fraction = elapsed(plan);
  const day = (
    <section className="day-planned" aria-label={t("Planned")}>
      <div className="day-heading day-planned-heading"><h2>{t("Planned")}</h2></div>
      <div className="day-line" aria-label={t("{length} free", { length: lengthWords(freeMinutes(plan)) })}>
        <div className="day-line-words">
          {/* How many are planned is Not Planned's count; a second count only confused (the UX review, 2026-10-24). */}
          <b>{t("{length} free", { length: lengthWords(freeMinutes(plan)) })}</b>
        </div>
        <span className="day-line-track" aria-hidden="true"><span style={{ width: `${fraction * 100}%` }} /></span>
        <div className="day-line-ends"><span>{timeOf(plan.dayStart, zone)}</span><span>{timeOf(plan.dayEnd, zone)}</span></div>
      </div>
      {plan.gaps.length === 0 && <p className="day-note">{t("Your day has ended. What is left waits in the list.")}</p>}
      {plan.gaps.map((gap) => {
        const here = plannedIn(gap, todos, plan.gaps, today, zone);
        const isNow = currentGap(plan) === gap;
        const minutes = gapMinutes(gap);
        return (
          <div key={gap.end.getTime()} className="day-gap">
            <div className="day-gap-head" {...headerDropProps(window, (ids) => planInto(gap, ids, here[0]?.id))}>
              <span className={`day-time${isNow ? " now" : ""}`}>{isNow ? t("Now") : timeOf(gap.start, zone)}</span>
              <span>{t("{length} free", { length: lengthWords(minutes) })}</span>
            </div>
            {here.map((todo, index) => row(todo, (ids, where) => planInto(gap, ids, where === "before" ? todo.id : here[index + 1]?.id)))}
            {here.length === 0 && <p className="day-note day-drop-hint">{t("Drag a to-do here to plan it.")}</p>}
          </div>
        );
      })}
    </section>
  );

  // Side by side wherever there is room, Home showing or not; else Not Planned above the day (more.css, a container
  // query on the view's own width).
  return <div className="day-view"><div className="day-halves">{notPlanned}{day}</div></div>;
}

/** One card of a glance: a small heading over what it shows. */
function GlanceCard({ title, children, onClick, label }: { title: string; children: ReactNode; onClick?: () => void; label?: string }) {
  const body = <><span className="glance-title">{title}</span>{children}</>;
  return onClick
    ? <button className="glance-card" onClick={onClick} aria-label={label}>{body}</button>
    : <div className="glance-card">{body}</div>;
}

function Bar({ value }: { value: number }) {
  return <span className="glance-bar" aria-hidden="true"><span style={{ width: `${Math.min(1, Math.max(0, value)) * 100}%` }} /></span>;
}

/** Today at a glance, beside its list: how much is done, the Inbox, the day's time, the deadlines. */
export function TodayGlance() {
  const { state, go } = useWindow();
  const { lists } = state;
  const now = useNow();
  const zone = currentZone();
  const progress = lists.todayProgress;
  const inbox = lists.openCount({ kind: "inbox" });
  const plan = dayPlan(state.today, zone, now);
  const todos = lists.sections({ kind: "today" }).flatMap((section) => section.todos).filter((todo) => todo.completedAt === undefined);
  const planned = todos.length - unplanned(todos, plan.gaps, state.today, zone).length;
  const deadlines = lists.deadlineGlance(4);
  return (
    <div className="glance" aria-label={t("Today at a Glance")}>
      <div className="glance-pair">
        <GlanceCard title={t("Done so far")}>
          <span className="glance-number"><b>{progress.done.toLocaleString(locale())}</b>{t("of {total}", { total: progress.total })}</span>
          <Bar value={progress.total ? progress.done / progress.total : 0} />
        </GlanceCard>
        <GlanceCard title={t("Inbox")} onClick={() => go({ kind: "inbox" })} label={t("Open the Inbox")}>
          {inbox === 0
            ? <span className="glance-number empty"><b>{t("Empty")}</b></span>
            : <span className="glance-number"><b>{inbox.toLocaleString(locale())}</b>{t("to decide")}</span>}
        </GlanceCard>
      </div>
      {state.isPro && (
        <GlanceCard title={t("The Day")}>
          <span className="glance-line"><b>{t("{length} left", { length: lengthWords(freeMinutes(plan)) })}</b><span>{t("{planned} of {total} planned", { planned, total: todos.length })}</span></span>
          <Bar value={elapsed(plan)} />
          <span className="glance-ends"><span>{timeOf(plan.dayStart, zone)}</span><span>{timeOf(plan.dayEnd, zone)}</span></span>
        </GlanceCard>
      )}
      {deadlines.items.length > 0 && (
        <GlanceCard title={t("Deadlines")} onClick={() => go({ kind: "due" })} label={t("Open Deadlines")}>
          {deadlines.items.map((todo) => (
            <span key={todo.id} className="glance-line">
              <span className="glance-text">{todo.title}</span>
              {todo.deadline && <span className={todo.deadline <= state.today ? "red" : ""}>{deadlineLabel(todo.deadline, state.today)}</span>}
            </span>
          ))}
          {deadlines.more > 0 && <span className="glance-more">{t("{count} more", { count: deadlines.more })}</span>}
        </GlanceCard>
      )}
    </div>
  );
}

/** A project at a glance, beside its to-dos: how much is done, its deadline, and each section's progress. */
export function ProjectGlance({ project }: { project: Project }) {
  const { state } = useWindow();
  const { lists } = state;
  const count = lists.doneCount(project);
  const parts = lists.sectionProgress(project);
  const deadline = isList(project) ? undefined : project.deadline;
  return (
    <div className="glance" aria-label={t("Overview")}>
      <div className="glance-pair">
        <GlanceCard title={t("Done so far")}>
          <span className="glance-number"><b>{count.done.toLocaleString(locale())}</b>{t("of {total}", { total: count.total })}</span>
          <Bar value={count.total ? count.done / count.total : 0} />
        </GlanceCard>
        {deadline && (
          <GlanceCard title={t("Deadline")}>
            <span className={`glance-number${deadline <= state.today ? " red" : ""}`}>
              <b>{new Date(`${deadline}T12:00:00`).toLocaleDateString(locale(), { weekday: "short", day: "numeric", month: "short" })}</b>
            </span>
            <span className={deadline <= state.today ? "red" : "glance-sub"}>{deadlineLabel(deadline, state.today)}</span>
          </GlanceCard>
        )}
      </div>
      {parts.some((part) => part.id !== undefined) && (
        <GlanceCard title={t("Sections")}>
          {parts.map((part) => (
            <span key={part.id ?? "none"} className="glance-part">
              <span className="glance-line"><span className="glance-text">{part.title ?? t("Not in a section")}</span><span>{t("{done} of {total}", { done: part.done, total: part.total })}</span></span>
              <Bar value={part.total ? part.done / part.total : 0} />
            </span>
          ))}
        </GlanceCard>
      )}
    </div>
  );
}
